// Public Policy Centre: the canonical index of ConfirmPH's published policies.
//
// Two sources, one list:
//   * the policies published on this site (content/policyVersions.js), each
//     with its version, effective date and every retired version that is
//     still kept readable;
//   * the Task 2 Legal Documents register. Where the register has published
//     a version of the same document, that published version is what is
//     listed. Register documents with no site page are listed too.
// Nothing unpublished is ever shown, and nothing is shown as "coming soon".
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, History, Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { fmtDate, humanise } from "@/lib/platformLegal";
import { POLICIES, formatEffective } from "@/content/policyVersions";
import { isPublishableContact } from "@/lib/operatorContacts";
import CachePublicShell from "@/components/CachePublicShell";
import SEO from "@/components/SEO";

export default function PolicyCentre() {
  const [register, setRegister] = useState(null);
  const [registerFailed, setRegisterFailed] = useState(false);

  useEffect(() => {
    api.get("/legal/policies")
      .then((r) => setRegister(r.data))
      .catch(() => setRegisterFailed(true));
  }, []);

  const rows = useMemo(() => buildRows(register?.policies || []), [register]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid="policy-centre">
      <CachePublicShell />
      <SEO title="Policy Centre — ConfirmPH"
           description="Every published ConfirmPH policy, with its version, effective date and earlier versions." />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Legal
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Policy Centre</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">
        Every policy ConfirmPH has published, with the version and effective date that applies. When
        a policy changes, the earlier version stays readable here, so what applied on any given day
        can always be checked. Acceptances already given stay recorded against the version that was
        accepted.
      </p>

      <OperatorBlock operator={register?.operator} />

      {!register && !registerFailed && (
        <p className="mt-6 flex items-center gap-2 text-xs text-slate-500" role="status">
          <Loader2 className="w-3 h-3 animate-spin" /> Checking the policy register…
        </p>
      )}

      <ul className="mt-8 space-y-3" data-testid="policy-centre-list">
        {rows.map((row) => (
          <li key={row.key} className="rounded-xl border border-slate-200 p-4"
              data-testid={`policy-${row.key}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="font-display text-lg font-semibold">{row.title}</h2>
                {row.summary && <p className="text-sm text-slate-600 mt-0.5">{row.summary}</p>}
              </div>
              <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2
                               py-0.5 text-xs text-emerald-800" data-testid={`policy-state-${row.key}`}>
                Current
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
              <span data-testid={`policy-version-${row.key}`}>Version {row.version}</span>
              <span>Effective {row.effectiveText}</span>
              {row.audience && <span>For: {row.audience}</span>}
            </div>
            <Link to={row.href}
                  className="mt-3 inline-flex items-center gap-1 text-sm text-slate-900
                             underline hover:no-underline"
                  data-testid={`policy-open-${row.key}`}>
              <FileText className="w-4 h-4" /> Read the policy
            </Link>
            {row.retired.length > 0 && (
              <div className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-600"
                   data-testid={`policy-retired-${row.key}`}>
                <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                  <History className="w-3 h-3" /> Retired versions
                </span>
                <ul className="mt-1 space-y-0.5">
                  {row.retired.map((r) => (
                    <li key={r.label}>
                      {r.href
                        ? <Link to={r.href} className="underline">{r.label}</Link>
                        : r.label}
                      <span className="text-slate-400"> · retired</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>

      {registerFailed && (
        <p className="mt-4 text-xs text-slate-500" data-testid="policy-centre-register-note">
          The policy register could not be reached just now. The policies above are the versions
          published on this site.
        </p>
      )}

      <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-2">
        <p>
          <Link to="/what-we-verify" className="underline" data-testid="policy-centre-verify">
            What ConfirmPH verifies
          </Link>{" "}
          ·{" "}
          <Link to="/report" className="underline" data-testid="policy-centre-report">
            Report a problem
          </Link>{" "}
          ·{" "}
          <Link to="/security" className="underline" data-testid="policy-centre-security">
            Security contact
          </Link>
        </p>
        <Link to="/" className="underline block">← Back to home</Link>
      </div>
    </div>
  );
}

function buildRows(registerPolicies) {
  const registerByType = Object.fromEntries(registerPolicies.map((p) => [p.doc_type, p]));
  const usedTypes = new Set();
  const rows = POLICIES.map((policy) => {
    const published = policy.registryDocType ? registerByType[policy.registryDocType] : null;
    if (published) {
      usedTypes.add(policy.registryDocType);
      return fromRegister(published, policy);
    }
    const [current, ...older] = policy.versions;
    return {
      key: policy.slug,
      title: policy.title,
      summary: null,
      audience: policy.audience,
      version: current.version,
      effectiveText: formatEffective(current.effective),
      href: policy.path,
      retired: older.map((v) => ({
        label: `Version ${v.version}, effective ${formatEffective(v.effective)}`,
        href: v.archive ? `/legal/archive/${policy.slug}/${v.archive}` : null,
      })),
    };
  });
  for (const published of registerPolicies) {
    if (!usedTypes.has(published.doc_type)) rows.push(fromRegister(published, null));
  }
  return rows;
}

function fromRegister(published, sitePolicy) {
  return {
    key: published.doc_type,
    title: published.title,
    summary: published.summary,
    audience: published.audience ? humanise(published.audience) : sitePolicy?.audience,
    version: published.version_number,
    effectiveText: fmtDate(published.effective_date),
    href: `/legal/policies/${published.doc_type}`,
    retired: (published.previous_versions || []).map((p) => ({
      label: `Version ${p.version_number}${p.effective_date ? `, effective ${fmtDate(p.effective_date)}` : ""}`,
      href: null,
    })),
  };
}

function OperatorBlock({ operator }) {
  if (!operator) return null;
  const privacy = operator.privacy || {};
  const privacyContact = isPublishableContact(privacy.contact) ? privacy.contact : null;
  const supportEmail = isPublishableContact(operator.support_email) ? operator.support_email : null;
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm"
         data-testid="policy-centre-operator">
      <div className="font-medium text-slate-900">{operator.operator_name}</div>
      {operator.disclosure && (
        <p className="mt-1 text-slate-700">{operator.disclosure}</p>
      )}
      <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
        {supportEmail && (
          <>
            <dt className="text-slate-500">Contact</dt>
            <dd className="text-slate-800 sm:text-right break-all">{supportEmail}</dd>
          </>
        )}
        {privacy.dpo_appointed && privacy.dpo_name ? (
          <>
            <dt className="text-slate-500">Data Protection Officer</dt>
            <dd className="text-slate-800 sm:text-right break-all" data-testid="policy-centre-dpo">
              {privacy.dpo_name}{privacyContact ? ` · ${privacyContact}` : ""}
            </dd>
          </>
        ) : privacyContact ? (
          <>
            <dt className="text-slate-500">Privacy requests</dt>
            <dd className="text-slate-800 sm:text-right break-all" data-testid="policy-centre-privacy">
              {privacyContact}
            </dd>
          </>
        ) : null}
        {operator.registered_office && (
          <>
            <dt className="text-slate-500">Registered office</dt>
            <dd className="text-slate-800 sm:text-right">{operator.registered_office}</dd>
          </>
        )}
      </dl>
      {!privacy.dpo_appointed && privacy.note && (
        <p className="mt-2 text-xs text-slate-600" data-testid="policy-centre-privacy-note">{privacy.note}</p>
      )}
    </div>
  );
}
